import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import MainLayout from '../../../../shared/Layouts/MainLayout'
import useEquipment from '../../hooks/useEquipment.ts'
import css from './liftPage.module.css'
import Breadcrumbs from '../../../../navigation/components/NavigationBreadcrumbs'
import EquipmentStatus from '../../components/Common/EquipmentStatus'
import LiftCard from './components/LiftCard'
import Tabs from '../../../../shared/Tabs'
import liftPageConstants from './liftPage.constants.ts'
import React from 'react'
import AboutLiftCardContent from './components/AboutLiftCardContent'
import EquipmentEventsTable from '../../components/Tables/EquipmentEventsTable'
import EquipmentMaintenanceTable from '../../components/Maintenance/EquipmentMaintenanceTable'
import Button from '@/shared/Buttons/Button'
import GoToLink from '@/assets/icons/goToLink.svg?react'
import AnalysisIcon from '@/assets/icons/search.svg?react'
import QuestionIconWithTooltip from '@/shared/Common/QuestionIconWithTooltip'
import LiftAnalyticsCardContent from '@/pages/Equipment/pages/LiftPage/components/LiftAnalyticsCardContent'
import urls from '@/navigation/urls.ts'
import SameAddressEquipmentDropdownInput from './components/SameAddressEquipmentDropdownInput/index.tsx'
import Divider from '@/shared/Common/Divider/index.tsx'
import SubscribeToNotificationsButton from '@/pages/Equipment/pages/LiftPage/components/SubscribeToNotificationsButton/index.tsx'
import AddToFavouriteButton from './components/AddToFavouriteButton/index.tsx'
import equipmentUtils from '../../equipment.utils.ts'
import LiftFilesTabContent from './components/LiftFilesTabContent/index.tsx'
import useTitle from '@/shared/hooks/useTitle'
import DataSourceTag from './components/DataSourceTag/index.tsx'

const LiftPage = () => {
  const navigate = useNavigate()

  const liftId = useParams().id
  const equipment = useEquipment(+liftId)
  useTitle(
    equipment
      ? `Лифт — ${equipmentUtils.getEquipmentAddress(equipment.location, {
          includeLocationDescription: true,
          includeCountry: false,
          includeCity: true,
          includeRegion: false,
          includeAddress: true,
        })}`
      : 'Лифт'
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTab =
    liftPageConstants.cardTabs.find((tab) => tab.id === searchParams.get('selectedTab'))?.id || ('about' as const)

  const renderContent = () => {
    switch (selectedTab) {
      case 'about':
        return <AboutLiftCardContent key={equipment.id} equipment={equipment} />
      case 'events':
        return <EquipmentEventsTable key={equipment.id} equipmentId={equipment.id} />
      case 'journals':
        return <EquipmentMaintenanceTable key={equipment.id} equipmentId={equipment.id} />
      case 'analytics':
        return <LiftAnalyticsCardContent key={equipment.id} equipmentId={equipment.id} />
      case 'files':
        return <LiftFilesTabContent key={equipment.id} equipmentId={equipment.id} />
      default:
        return null
    }
  }

  const handleSelectedTabChange = (value: string) => {
    const newSearchParams = new URLSearchParams()
    newSearchParams.set('selectedTab', value)
    setSearchParams(newSearchParams, { replace: true })
  }

  if (!equipment) return null

  return (
    <MainLayout>
      <div className={css.wrapper}>
        <div className="flex-lines gap16 height100">
          <div className="flex-space-between">
            <Breadcrumbs
              withBackButton={true}
              lastMatch={{
                pathname: '/',
                handle: {
                  canNavigate: false,
                  crumb: () =>
                    equipmentUtils.getEquipmentAddress(equipment.location, {
                      includeCountry: true,
                      includeLocationDescription: true,
                      includeCity: true,
                      includeRegion: true,
                      includeAddress: true,
                    }),
                },
                id: equipment.id.toString(),
              }}
            />
          </div>

          <div className="flex-lines gap8">
            <div className="inline-flex-gap gap8">
              <DataSourceTag protocol={equipment.protocolId} />
              <EquipmentStatus equipment={equipment} />

              {equipment.state === 'DISCONNECTED' && (
                <>
                  <p className="text-nm text-warning">
                    Лифт отключился от платформы. Отображаются последние полученные значения телеметрии.
                  </p>
                  <QuestionIconWithTooltip
                    text={`"Лифт отключился" значит, что ранее он отправлял телеметрию, но соединение с платформой было прервано. Такая ситуация возможна при перебоях в подключении к интернету у станции, либо при отключении станции.`}
                    position="bottom"
                  />
                </>
              )}
            </div>
            <div className="inline-flex-gap flex-space-between width100">
              <div className="inline-flex-gap gap8">
                <SameAddressEquipmentDropdownInput
                  liftId={+liftId}
                  onSelect={(val) => navigate(urls.liftScreen(val), { replace: true })}
                />
                <AddToFavouriteButton equipmentId={equipment.id} />
              </div>
              <div className="inline-flex-gap gap8 center">
                {equipment.protocolId !== 'lkds' && (
                  <Button
                    size="default"
                    type="default"
                    text="Мониторинг"
                    icon={<GoToLink />}
                    onClick={() => navigate(urls.liftMonitoring(equipment.id))}
                  />
                )}
                <Button
                  size="default"
                  type="default"
                  text="Анализ"
                  icon={<AnalysisIcon />}
                  onClick={() => navigate(urls.liftAnalysis(equipment.id))}
                />
                <Divider />
                <SubscribeToNotificationsButton equipmentId={equipment.id} />
              </div>
            </div>
          </div>
          <div className="inline-flex-gap gap24" style={{ minHeight: 0, flex: 1 }}>
            <div className={css.mainCard} data-selected-tab={selectedTab}>
              <div className={css.tabs}>
                <Tabs tabs={liftPageConstants.cardTabs} onSelect={handleSelectedTabChange} selectedTab={selectedTab} />
              </div>
              {renderContent()}
            </div>
            <LiftCard equipmentId={equipment.id} key={equipment.id} />
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

export default LiftPage
